import type { S2PElevateSession, S2PElevateSlide } from "@/data/s2pElevateAI";

const guideSource = "facilitator-field-guide";
const commitment = "My next test is ___. I’ll use ___. I’ll judge it by ___. I need help with ___.";
const activityIds = ["level-fit-huddle", "session-3-do-this-first", "session-5-metric-loop-build"];
const discussionIds = ["showcase-debrief", "qa-3", "qa-4"];

const savedDraft = (rockline: boolean, corrected: boolean) => {
  const handoff = rockline ? "Shift handoff" : "Customer handoff";
  const second = rockline ? "Confirm next-shift coverage" : "Confirm next customer review";
  return corrected
    ? `${handoff} | Maya | CONFLICT: September 30 [N1] / October 1 [T1] | Ask which date controls.\n${second} | UNKNOWN owner | UNKNOWN date [N2] | Ask the reviewer to assign both.\nDraft only. No messages sent or records changed.`
    : `${handoff} | Maya | September 30 | N1/T1 | No issue.\n${second} | Lee | October 2 | N2 | No issue.\nDraft only. No messages sent or records changed.`;
};

// Applied only to the two ELEVATE decks, after audience-specific content is resolved.
export const applyElevateFacilitation = (slide: S2PElevateSlide, rockline = false): S2PElevateSlide => {
  if (slide.session < 3) return slide;
  const provider = rockline ? "Microsoft Copilot" : "ChatGPT or Claude";
  const patch: Partial<S2PElevateSlide> = {};

  if (slide.kind === "session") {
    const focus = { 3: "Choose a tool: match the source and review standard to the Job.", 4: "Control one run: set its boundary and review the evidence.", 5: "Improve one result: compare the evidence and change one instruction." };
    patch.opening = {
      focus: focus[slide.session as 3 | 4 | 5],
      progress: "Share one attempt since last time: your goal, what happened, and where you got stuck." + (slide.session === 3 ? "" : " If you made a commitment, report what happened."),
      participation: "Take a moment to think, then speak, use chat, or write privately. Unfinished attempts count; you may pass.",
    };
  }

  if (slide.kind === "showcase-example") {
    patch.eyebrow = `Optional extension · Session ${slide.session}`;
  }

  if (activityIds.includes(slide.id) && slide.practice) {
    const session = slide.session;
    patch.eyebrow = "Worked example + your Job · 15-minute activity";
    patch.practice = {
      ...slide.practice,
      decisionPause: session === 3
        ? `Which approved source and confirmed access should determine the ${provider} path? Give your reason before opening the example.`
        : session === 4
          ? "The notes say September 30; the tracker says October 1. What should the draft show, and who decides which date controls?"
          : "Compare your two runs, or the saved drafts A and B below. Which result is better supported? Give your evidence before revealing the review target.",
      offline: {
        instructions: session === 3
          ? "No working tool access? Use the fictional setup and source inputs above. Mark unsupported access claims in the saved decision, write a corrected choice, and explain what evidence would change it."
          : session === 4
            ? "No working tool access? Review saved draft A against N1, T1, and N2 above. Mark unsupported claims, write one correction, and record the question for the reviewer. Keep your annotated draft for Session 5."
            : "No working tool access? Compare saved drafts A and B against the same N1, T1, and N2 inputs above. Check source traceability, invented values, and both issue flags. Propose one instruction change, then record proceed, revise, or stop with evidence.",
        outputs: session === 3 ? [{
          label: "Saved decision · deliberately flawed",
          text: rockline
            ? "Use a Copilot agent with all Microsoft 365 work sources. Assume it can already read the notes and tracker because Copilot Chat file upload works. Reviewer: operations-review owner. Boundary: draft only."
            : "Use Claude with the task’s files. Assume source retrieval is already verified because Claude is approved. Reviewer: meeting owner. Boundary: draft only.",
        }] : [
          { label: "Saved draft A · deliberately flawed", text: savedDraft(rockline, false) },
          ...(session === 5 ? [{ label: "Saved draft B · proposed revision", text: savedDraft(rockline, true) }] : []),
        ],
      },
    };
    patch.note = `${slide.note ?? ""} No working tool access? Open the saved-output review below and practice the same decisions. Count it as a review exercise, not a completed AI run.`.trim();
  }

  if (discussionIds.includes(slide.id)) {
    patch.debrief = {
      timing: slide.session === 3
        ? "13 minutes: share and discuss. Final 2 minutes: write your next test."
        : slide.session === 4
          ? "3 minutes: colleague test. 10 minutes: share and discuss. Final 2 minutes: write your next test."
          : "13 minutes: share and discuss, including up to 5 minutes for the optional handoff. Final 2 minutes: write your next test.",
      share: "In a one-minute share, show your output, explain one decision, and name your next change. Unfinished work and written or chat responses count.",
      transfer: "Where could you use a pattern from someone else’s example in your own work?",
      commitment,
    };
    patch.note = slide.session === 4
      ? "For the colleague test, use your own approved access. If access is blocked, exchange written methods and the saved sample for a review-only test. Record the gap; do not share credentials."
      : slide.session === 5
        ? "Keep the same inputs and review checks. Record proceed, revise, or stop. Optional handoff time comes from this discussion; protect the closing commitment."
        : slide.note;
  }

  return Object.keys(patch).length ? { ...slide, ...patch, sourceIds: [...new Set([...slide.sourceIds, guideSource])] } : slide;
};

export const applyElevateFacilitationTiming = (session: S2PElevateSession): S2PElevateSession => {
  if (session.id < 3 || !session.teachingPlan) return session;
  return {
    ...session,
    teachingPlan: [
      { title: "Participant progress and today's focus", minutes: 2 },
      ...session.teachingPlan.map((part, index) => ({
        ...part,
        minutes: part.minutes - (index === 0 ? 2 : 0),
        title: part.title === "Compare tool fit and work through three use cases"
          ? "Compare tool fit with one worked example; extensions if useful"
          : part.title === "Three controlled-workflow use cases"
            ? "Worked-example review and optional extensions"
            : part.title,
      })),
    ],
  };
};
